// =============================================================================
// RSL Parser — human-readable script → RoutineNode definitions
// =============================================================================
import type {
  RoutineNode, AttackNode, DamageNode, ModifierComponent,
  ConditionalBonusDamageNode, ConditionalBonusDamageCondition,
  BonusAttackNode, BonusAttackCondition, WeaponSpecialProperty,
} from '../../../backend/shared/types/index';

export interface ParseResult {
  success: boolean;
  nodes:   RoutineNode[];
  errors:  string[];
}

export function parseRSLScript(script: string): ParseResult {
  const errors: string[] = [];
  const nodes:  RoutineNode[] = [];

  if (!script.trim()) return { success: true, nodes: [], errors: [] };

  const lines = script.split('\n')
    .map(l => l.trim())
    .filter(l => l && !l.startsWith('ROUTINE') && !l.startsWith('SYSTEM'));

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('ATTACK')) {
      const r = parseAttackBlock(lines, i);
      r.error ? errors.push(r.error) : r.node && nodes.push(r.node);
      i = r.nextIndex;
    } else if (line.startsWith('IF ')) {
      const r = parseConditionalBlock(lines, i);
      r.error ? errors.push(r.error) : r.node && nodes.push(r.node);
      i = r.nextIndex;
    } else if (line.startsWith('BONUS_ATTACK')) {
      const r = parseBonusAttackBlock(lines, i);
      r.error ? errors.push(r.error) : r.node && nodes.push(r.node);
      i = r.nextIndex;
    } else {
      i++;
    }
  }
  return { success: errors.length === 0, nodes, errors };
}

// ---------------------------------------------------------------------------
// Component parsers
// ---------------------------------------------------------------------------

/**
 * Parse modifier components from a roll or damage expression string.
 * Handles: `+ stat_key`, `+ stat_key (descriptor)`, `+ N`, `+ N (descriptor)`,
 * `- N`, etc.  Returns components and any leftover text (notes).
 */
function parseComponents(
  expr: string,
): { components: ModifierComponent[]; remainder: string } {
  const components: ModifierComponent[] = [];
  // Pattern: optional sign, then either a stat_key (word chars only, starting with letter)
  // or a number, optionally followed by a descriptor in parens.
  const TOKEN = /^([+-])\s*(?:([a-zA-Z][a-zA-Z0-9_]*)(?:\s*\(([^)]+)\))?|(\d+)(?:\s*\(([^)]+)\))?)\s*/;

  let rest = expr.trim();
  while (rest.length > 0) {
    const m = rest.match(TOKEN);
    if (!m) break;

    const sign       = m[1];
    const statKey    = m[2];
    const statDesc   = m[3];
    const numStr     = m[4];
    const numDesc    = m[5];

    if (statKey) {
      components.push({
        id:         crypto.randomUUID(),
        type:       'stat_ref',
        statKey,
        descriptor: statDesc || undefined,
      });
    } else if (numStr !== undefined) {
      const value = parseInt(sign + numStr);
      if (value !== 0) {
        components.push({
          id:         crypto.randomUUID(),
          type:       'manual',
          value,
          descriptor: numDesc || undefined,
        });
      }
    }
    rest = rest.slice(m[0].length);
  }
  return { components, remainder: rest.trim() };
}

/**
 * Parse the full damage content inside `DAMAGE { … }`.
 * Returns a DamageNode (without id/critMult — caller fills those in).
 */
function parseDamageContent(
  content: string,
  defaultCritMult: number,
): DamageNode {
  // 1. Extract size tag [size:xxx] and special damage tag [special:xxx]
  const sizeMatch = content.match(/\[size:([a-z]+)\]/i);
  const weaponSize = sizeMatch ? (sizeMatch[1] as any) : undefined;
  content = content.replace(/\[size:[a-z]+\]/gi, '').trim();

  const specialMatch = content.match(/\[special:([a-z_]+)\]/i);
  const specialType = specialMatch ? (specialMatch[1] as any) : undefined;
  content = content.replace(/\[special:[a-z_]+\]/gi, '').trim();

  // 2. Extract leading value — dice expression ("2d6", "1d8") or plain integer ("2")
  let diceExpr = '';
  const diceMatch = content.match(/^(\d+d\d+)\s*/i);
  if (diceMatch) {
    // Standard dice notation
    diceExpr = diceMatch[1];
    content = content.slice(diceMatch[0].length);
  } else {
    // Plain integer — fixed-value damage (e.g. "2" for 2 negative levels)
    const flatMatch = content.match(/^(\d+)\s*/);
    if (flatMatch) {
      diceExpr = flatMatch[1];
      content = content.slice(flatMatch[0].length);
    }
  }

  // 3. Parse modifier components
  const { components, remainder } = parseComponents(content);
  const notes = remainder;

  return {
    id:            crypto.randomUUID(),
    diceExpr,
    notes,
    critMult:      defaultCritMult,
    modComponents: components.length > 0 ? components : [],
    weaponSize,
    specialType,
  };
}

/** Extract all damage nodes from a `DAMAGE { … }` substring. */
function parseDamageList(blockStr: string, defaultCritMult: number): DamageNode[] {
  // Content is between the first { and the matching }
  const open  = blockStr.indexOf('{');
  const close = blockStr.lastIndexOf('}');
  if (open === -1 || close === -1) return [];
  const inner = blockStr.slice(open + 1, close).trim();
  // Multiple damage entries can be joined with ' + ' at the value boundary.
  // Split on ' + ' when the next token is a dice expression ("2d6") or a plain
  // integer ("2") that starts a new entry — not a modifier component like "+ 5 (Fire)".
  // A leading plain integer followed by end-of-string, whitespace, or a tag '[' is
  // a new entry; one followed immediately by more digits/letters is a modifier.
  const entries = inner.split(/\s+\+\s+(?=\d+d\d+|\d+(?:\s|$|\[))/i);
  return entries.map(e => parseDamageContent(e.trim(), defaultCritMult));
}

/**
 * Parse roll components from an attack roll expression like
 * `d20 + bab (BAB Max) + str_mod + 5 (Enhancement)`.
 */
function parseRollComponents(rollExpr: string): ModifierComponent[] {
  const withoutD20 = rollExpr.replace(/^d20\s*/, '');
  return parseComponents(withoutD20).components;
}

// ---------------------------------------------------------------------------
// Block parsers
// ---------------------------------------------------------------------------

function collectBlock(
  lines: string[], startIdx: number,
): { blockLines: string[]; nextIndex: number } {
  let i = startIdx;
  const blockLines: string[] = [];
  let depth = 1;

  // The header line already consumed the opening '{'; advance past it.
  i++;
  while (i < lines.length && depth > 0) {
    const line = lines[i];
    depth += (line.match(/\{/g) ?? []).length;
    depth -= (line.match(/\}/g) ?? []).length;
    if (depth > 0) blockLines.push(line);
    i++;
  }
  return { blockLines, nextIndex: i };
}

function parseAttackBlock(
  lines: string[], startIdx: number,
): { node?: AttackNode; error?: string; nextIndex: number } {
  const firstLine = lines[startIdx];
  const labelMatch = firstLine.match(/ATTACK\s+"([^"]+)"/);
  if (!labelMatch) {
    return { error: `Invalid ATTACK syntax: ${firstLine}`, nextIndex: startIdx + 1 };
  }
  const label = labelMatch[1];

  const { blockLines, nextIndex } = collectBlock(lines, startIdx);

  let roll         = 'd20';
  let vsTarget     = 'AC';
  let critMin      = 20;
  let noCrit       = false;
  const weaponProperties: WeaponSpecialProperty[] = [];
  const damage:       DamageNode[] = [];
  const onCritExtra:  DamageNode[] = [];

  for (const bl of blockLines) {
    if (bl.startsWith('roll:')) {
      const parts = bl.split(' vs: ');
      roll    = parts[0].replace(/^roll:\s*/, '').trim();
      vsTarget = parts[1]?.trim() ?? 'AC';

    } else if (bl === 'no_crit') {
      noCrit = true;

    } else if (bl.startsWith('crit:')) {
      // Accept: "crit: 19-20" or old format "crit: 19-20  mult: x2"
      const critPart = bl.match(/crit:\s*(\d+)-20/);
      if (critPart) {
        critMin = parseInt(critPart[1]);
        noCrit  = false;
      }

    } else if (bl.startsWith('property:')) {
      const prop = bl.replace(/^property:\s*/i, '').trim().toLowerCase();
      if ((prop === 'vorpal' || prop === 'sharpness') && !weaponProperties.includes(prop)) {
        weaponProperties.push(prop);
      }

    } else if (bl.startsWith('on_hit:') && bl.includes('DAMAGE')) {
      damage.push(...parseDamageList(bl.slice(bl.indexOf('DAMAGE')), 2));

    } else if (bl.startsWith('on_crit:') && bl.includes('DAMAGE')) {
      // on_crit rows use critMult 1 (they are never multiplied again)
      onCritExtra.push(...parseDamageList(bl.slice(bl.indexOf('DAMAGE')), 1));
    }
  }

  const attackComponents = parseRollComponents(roll);

  const node: AttackNode = {
    type:             'attack',
    id:               crypto.randomUUID(),
    label,
    attackMod:        attackComponents.reduce((s, c) => s + (c.type === 'manual' ? (c.value ?? 0) : 0), 0),
    attackComponents: attackComponents.length > 0 ? attackComponents : undefined,
    vsTarget,
    critRange:        [critMin, 20],
    noCrit:           noCrit || undefined,
    weaponProperties: weaponProperties.length ? weaponProperties : undefined,
    damage:           damage.length > 0 ? damage : [{ id: crypto.randomUUID(), diceExpr: '', notes: '', critMult: 2, modComponents: [] }],
    onCritExtra:      onCritExtra,
  };

  return { node, nextIndex };
}

function parseConditionalBlock(
  lines: string[], startIdx: number,
): { node?: ConditionalBonusDamageNode; error?: string; nextIndex: number } {
  const firstLine = lines[startIdx];

  // Supports:
  //   IF CRIT_HITS {  // label         (no threshold — always >= 1)
  //   IF HITS >= 3 {  // label
  const critHitsMatch = firstLine.match(/IF\s+CRIT_HITS\s*{(.*)$/);
  const hitsMatch     = firstLine.match(/IF\s+HITS\s+(>=|<=|=)\s+(\d+)\s*{(.*)/);

  if (!critHitsMatch && !hitsMatch) {
    return { error: `Invalid IF syntax: ${firstLine}`, nextIndex: startIdx + 1 };
  }

  let condition: ConditionalBonusDamageCondition;
  let labelStr: string;

  if (critHitsMatch) {
    condition = { subject: 'crit_hits', op: 'gte', value: 1 };
    labelStr  = critHitsMatch[1];
  } else {
    const opStr  = hitsMatch![1];
    condition = {
      subject: 'hits',
      op:      opStr === '>=' ? 'gte' : opStr === '<=' ? 'lte' : 'eq',
      value:   parseInt(hitsMatch![2]),
    };
    labelStr = hitsMatch![3];
  }

  const label = labelStr.replace(/^[\s/]+/, '').trim() || undefined;

  const { blockLines, nextIndex } = collectBlock(lines, startIdx);

  const damage: DamageNode[] = [];
  for (const bl of blockLines) {
    if (bl.includes('BONUS_DAMAGE')) {
      damage.push(...parseDamageList(bl.slice(bl.indexOf('BONUS_DAMAGE')), 2));
    }
  }

  const node: ConditionalBonusDamageNode = {
    type:      'conditional_damage',
    id:        crypto.randomUUID(),
    condition,
    damage:    damage.length > 0 ? damage : [{ id: crypto.randomUUID(), diceExpr: '', notes: '', critMult: 2, modComponents: [] }],
    label,
  };
  return { node, nextIndex };
}

function parseBonusAttackBlock(
  lines: string[], startIdx: number,
): { node?: BonusAttackNode; error?: string; nextIndex: number } {
  const firstLine = lines[startIdx];

  // Supports:
  //   BONUS_ATTACK when CRIT_HITS {  // label    (no threshold)
  //   BONUS_ATTACK when HITS >= 1 {  // label
  const critHitsMatch = firstLine.match(/BONUS_ATTACK\s+when\s+CRIT_HITS\s*{(.*)/);
  const hitsMatch     = firstLine.match(/BONUS_ATTACK\s+when\s+HITS\s+(>=|<=|=)\s+(\d+)\s*{(.*)/);

  if (!critHitsMatch && !hitsMatch) {
    return { error: `Invalid BONUS_ATTACK syntax: ${firstLine}`, nextIndex: startIdx + 1 };
  }

  let condition: BonusAttackCondition;
  let labelStr: string;

  if (critHitsMatch) {
    condition = { subject: 'crit_hits', op: 'gte', value: 1 };
    labelStr  = critHitsMatch[1];
  } else {
    const opStr = hitsMatch![1];
    condition = {
      subject: 'hits',
      op:      opStr === '>=' ? 'gte' : opStr === '<=' ? 'lte' : 'eq',
      value:   parseInt(hitsMatch![2]),
    };
    labelStr = hitsMatch![3];
  }

  const label = labelStr.replace(/^[\s/]+/, '').trim() || undefined;

  const { blockLines, nextIndex } = collectBlock(lines, startIdx);

  // Find nested ATTACK block
  const atkIdx = blockLines.findIndex(l => l.match(/^ATTACK\s+"/));
  if (atkIdx === -1) {
    return { error: `BONUS_ATTACK missing nested ATTACK block`, nextIndex };
  }

  const nested = parseAttackBlock(blockLines, atkIdx);
  if (nested.error || !nested.node) {
    return { error: nested.error ?? 'Failed to parse nested ATTACK', nextIndex };
  }

  const node: BonusAttackNode = {
    type:      'bonus_attack',
    id:        crypto.randomUUID(),
    condition,
    attack:    nested.node,
    label,
  };
  return { node, nextIndex };
}
